<script setup>
// Builder.io
import { Content, isPreviewing, isEditing } from '@builder.io/sdk-vue';
// Internal
import { getComponents } from '@/composables/builder/blocks';
import { getBuilderContent } from '@/composables/builder/content';
import { getSeoOptions } from '@/composables/builder/seo';
import { getModelFromURL } from '@/composables/builder/model';
import { useGlobalStore } from '@/stores/global';

const { $config } = useNuxtApp();
const { urlExceptions } = useGlobalStore() || {};

const route = useRoute();

const model_options = getModelFromURL(route.path, 'page', $config, urlExceptions);
const model = model_options.model ? model_options.model : 'page';

const can_show_content = ref(isPreviewing(route.fullPath));
const builder_content = ref(isPreviewing(route.fullPath));

const { content, api_key, error } = await getBuilderContent();

if (error.statusCode === 500) {
    throw error;
}

if (!isPreviewing(route.fullPath)) {
    if (error) {
        throw error;
    }
    if (content.value?.data && model !== 'nuxt') {
        builder_content.value = content.value;
        const seoOptions = getSeoOptions(content.value, model);
        if (seoOptions && seoOptions.title) {
            if (content.value.data?.seo?.seo?.removeTitleTail || seoOptions.title.includes('|')) {
                useHead({
                    titleTemplate: '%s',
                });
            }
            useSeoMeta({
                title: seoOptions.title,
                ogTitle: seoOptions.title,
                description: seoOptions.description,
                ogDescription: seoOptions.description,
                ogImage: seoOptions.webpage?.image || '/social.jpg',
                twitterCard: 'summary_large_image',
            });
        }
    } else {
        builder_content.value = false;
    }
    can_show_content.value = content ? true : isPreviewing(route.fullPath);
    builder_content.value = content ? content.value : isPreviewing(route.fullPath);
}

const editorUpdate = (event) => {
    const { data } = event.data;
    const new_content = { ...content.value };
    new_content.data = { ...new_content.data, ...data.data.data };
    content.value = new_content;
    can_show_content.value = true;
};

const builderMessage = (event) => {
    if (event.data?.type !== 'builder.contentUpdate') return;
    editorUpdate(event);
};

onMounted(() => {
    if (isPreviewing(route.fullPath)) {
        window.addEventListener('message', builderMessage, '*');
    }
});

onBeforeUnmount(() => {
    if (isPreviewing(route.fullPath)) {
        window.removeEventListener('message', builderMessage);
    }
});
</script>

<template>
    <ProjectPage :page="content" :data-model="model" v-if="can_show_content">
        <TemplatesBlogHero :page="content" v-if="model === 'blog'" />
        <Content
            :api-key="api_key"
            :model="model"
            :content="builder_content"
            :customComponents="getComponents(api_key)"
        />
        <template v-slot:footer v-if="model === 'blog'">
            <TemplatesBlogFooter :page="content" />
        </template>
    </ProjectPage>
</template>

<style lang="scss">

.page-wrapper {
    [class*='builder-pixel'] {
        position: absolute;
    }
}

.page-wrapper[data-model='page'] {
    background-color: var(--cream);
}

.page-wrapper[data-model='blog'] {
    
    background-color: var(--cream);
    color: var(--teal);
    
    .breadcrumbs {
        z-index: 1;
    }

    .template-blog-hero {
        margin-bottom: 2.25rem;
    }
    .root-container {
        max-width: 1130px;
        width: calc(1130 / 1440 * 100vw);
        margin: auto;
        --block-padding: 2.25rem;

        .builder-blocks {
            margin-top: 0 !important;
        }

        .container {
            max-width: 100%;
            .inner-container {
                max-width: 100%;
            }
        }

        .builder-text {
            max-width: 960px;
            padding: 0;

            @media (min-width: $tablet) {
                padding: var(--block-padding) 30px;
            }
        }

       .builder-block > picture:first-child {
            img {
                border-radius: var(--radius);
            }
        }
    }

    @media (max-width: $tablet) {
        .root-container {
            width: 90vw;
        }
    }
}
</style>
